import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { CLIENT_PHOTOS } from '@/lib/index';
import { Input } from '@/components/ui/input';

export default function Clients() {
  const [selected, setSelected] = useState<null | typeof CLIENT_PHOTOS[0]>(null);
  const [search, setSearch] = useState('');
  const [sportFilter, setSportFilter] = useState('Todos');

  const sports = ['Todos', ...Array.from(new Set(CLIENT_PHOTOS.map((p) => p.sport)))];

  const filtered = CLIENT_PHOTOS.filter((p) => {
    const matchSearch = p.clientName.toLowerCase().includes(search.toLowerCase()) ||
      p.sport.toLowerCase().includes(search.toLowerCase()) ||
      p.teamName.toLowerCase().includes(search.toLowerCase());
    const matchSport = sportFilter === 'Todos' || p.sport === sportFilter;
    return matchSearch && matchSport;
  });

  const currentIdx = selected ? filtered.findIndex((p) => p.id === selected.id) : -1;

  return (
    <div className="min-h-screen bg-background py-16">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 40 }}
          className="text-center mb-12"
        >
          <p className="text-primary font-bold text-xs tracking-widest uppercase mb-3">GALERÍA</p>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight text-foreground mb-4">
            NUESTROS<br /><span className="text-primary">CLIENTES</span>
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Equipos y empresas que confían en <strong className="text-foreground">Dos de Tres</strong> para sus uniformes.
            Calidad premium, diseños únicos.
          </p>
        </motion.div>

        {/* Search & Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-10 max-w-xl mx-auto">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar equipo o deporte..."
              className="pl-9 bg-card border-border"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {sports.map((s) => (
              <button
                key={s}
                onClick={() => setSportFilter(s)}
                className={`px-3 py-2 text-xs font-bold tracking-widest rounded transition-all duration-200 ${
                  sportFilter === s
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card border border-border text-muted-foreground hover:border-primary hover:text-primary'
                }`}
              >
                {s.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-16">
          {filtered.map((photo, i) => (
            <motion.div
              key={photo.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07, type: 'spring', stiffness: 250, damping: 35 }}
              onClick={() => setSelected(photo)}
              className="group cursor-pointer bg-card border border-border rounded-xl overflow-hidden hover:border-primary/50 transition-all duration-300"
            >
              <div className="relative h-52 overflow-hidden bg-muted">
                <img
                  src={photo.imageSrc}
                  alt={photo.teamName}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card/90 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-foreground font-black text-sm tracking-widest uppercase">{photo.teamName}</p>
                  <span className="text-[10px] text-primary font-bold tracking-wider">{photo.sport.toUpperCase()}</span>
                </div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-primary/90 rounded-full p-3">
                    <Search className="text-primary-foreground" size={20} />
                  </div>
                </div>
              </div>
              <div className="p-4">
                <p className="text-xs text-muted-foreground">{photo.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <p className="text-muted-foreground text-lg">No se encontraron resultados.</p>
          </div>
        )}

        {/* Upload CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-card border border-primary/30 rounded-xl p-8 text-center"
        >
          <h3 className="text-xl font-black uppercase tracking-widest text-foreground mb-3">
            ¿YA ERES CLIENTE DE DOS DE TRES?
          </h3>
          <p className="text-muted-foreground mb-6">
            Comparte fotos de tus uniformes y aparece en nuestra galería. ¡Escríbenos por WhatsApp o Instagram!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://wa.me/528333007124?text=Hola,%20quiero%20compartir%20fotos%20de%20mis%20uniformes"
              target="_blank"
              rel="noreferrer"
              className="px-8 py-3 bg-primary text-primary-foreground font-black text-xs tracking-widest uppercase rounded hover:bg-primary/90 transition-all"
            >
              ENVIAR FOTOS POR WHATSAPP
            </a>
            <a
              href="https://instagram.com/23_dosdetres"
              target="_blank"
              rel="noreferrer"
              className="px-8 py-3 border border-primary text-primary font-bold text-xs tracking-widest uppercase rounded hover:bg-primary/10 transition-all"
            >
              @23_DOSDETRES
            </a>
          </div>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-2xl w-full bg-card rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setSelected(null)}
                className="absolute top-4 right-4 z-10 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/80 transition-colors"
              >
                <X size={16} />
              </button>
              <img
                src={selected.imageSrc}
                alt={selected.teamName}
                className="w-full h-72 object-cover"
              />
              <div className="p-6">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-xl font-black uppercase tracking-widest text-foreground">{selected.teamName}</h3>
                    <span className="text-xs text-primary font-bold tracking-widest">{selected.sport.toUpperCase()}</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">{selected.description}</p>
              </div>

              {/* Prev / Next */}
              <div className="absolute top-1/2 -translate-y-1/2 left-2">
                <button
                  onClick={(e) => { e.stopPropagation(); if (currentIdx > 0) setSelected(filtered[currentIdx - 1]); }}
                  disabled={currentIdx === 0}
                  className="w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white disabled:opacity-30 hover:bg-black/80 transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
              </div>
              <div className="absolute top-1/2 -translate-y-1/2 right-2">
                <button
                  onClick={(e) => { e.stopPropagation(); if (currentIdx < filtered.length - 1) setSelected(filtered[currentIdx + 1]); }}
                  disabled={currentIdx === filtered.length - 1}
                  className="w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white disabled:opacity-30 hover:bg-black/80 transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
